/**
 * Email Fingerprint Utility
 * 
 * Generates a unique identifier for the currently open email.
 * Primary: internetMessageId (RFC-2822 Message-ID, globally unique)
 * Fallback: SHA-256 hash of email metadata
 */

/**
 * Get the internetMessageId from the currently open Outlook email item.
 * This is the most reliable unique identifier for an email.
 */
export function getInternetMessageId(): string | undefined {
    try {
        const item = Office.context.mailbox.item;
        if (!item) return undefined;

        // internetMessageId is available on read items (Mailbox 1.1+)
        return (item as any).internetMessageId || undefined;
    } catch (e) {
        console.warn("[emailFingerprint] Failed to get internetMessageId:", e);
        return undefined;
    }
}

/**
 * Generate a fallback fingerprint from email metadata fields.
 * Uses a simple hash when internetMessageId is unavailable.
 */
export async function generateFallbackFingerprint(emailData: {
    subject?: string;
    sender?: string;
    receivedDate?: string;
    rawContentText?: string;
}): Promise<string> {
    const raw = [
        emailData.subject || "",
        emailData.sender || "",
        emailData.receivedDate || "",
        (emailData.rawContentText || "").slice(0, 200), // First 200 chars of body
    ].join("|");

    // Use SubtleCrypto SHA-256 if available, otherwise simple hash
    if (typeof crypto !== "undefined" && crypto.subtle) {
        const encoder = new TextEncoder();
        const data = encoder.encode(raw);
        const hashBuffer = await crypto.subtle.digest("SHA-256", data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return "fallback-" + hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    }

    // Simple fallback hash (djb2)
    let hash = 5381;
    for (let i = 0; i < raw.length; i++) {
        hash = ((hash << 5) + hash + raw.charCodeAt(i)) | 0;
    }
    return "fallback-" + Math.abs(hash).toString(16);
}

/**
 * Get the best available fingerprint for the currently open email.
 * Prefers internetMessageId, falls back to content hash.
 */
export async function getEmailFingerprint(emailData?: {
    subject?: string;
    sender?: string;
    receivedDate?: string;
    rawContentText?: string;
}): Promise<string> {
    // Try internetMessageId first (best identifier)
    const messageId = getInternetMessageId();
    if (messageId) {
        return messageId;
    }

    // Fallback to content-based hash
    if (emailData) {
        return generateFallbackFingerprint(emailData);
    }

    // Last resort: use itemId (changes on folder move, but better than nothing)
    try {
        const itemId = Office.context.mailbox.item?.itemId;
        if (itemId) return `itemId-${itemId}`;
    } catch (e) {
        // ignore
    }

    return `unknown-${Date.now()}`;
}
